import { TypedDocumentNode, gql } from '@apollo/client';
import { useMemo } from 'react';
import { FormattedMessage, MessageDescriptor } from 'react-intl';
import {
  Outlet,
  generatePath,
  useOutletContext,
  useParams,
} from 'react-router-dom';

import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import {
  FOOTBALL_COUNTRY_SHOW,
  FOOTBALL_COUNTRY_SHOW_CARDS,
  FOOTBALL_COUNTRY_SHOW_FIXTURES,
  FOOTBALL_COUNTRY_SHOW_TEAM,
} from '@sorare/core/src/constants/routes';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useSettlableWalletsSearch } from '@sorare/core/src/hooks/useSettlableWalletsSearch';
import { glossary, navLabels } from '@sorare/core/src/lib/glossary';

import {
  CountryQuery,
  CountryQueryVariables,
} from './__generated__/layout.graphql';

export const COUNTRY_QUERY = gql`
  query CountryQuery($countryCode: String!) {
    football {
      nationalTeam(countryCode: $countryCode) {
        slug
        name
        country {
          slug
          code
          name
        }
      }
    }
  }
` as TypedDocumentNode<CountryQuery, CountryQueryVariables>;

export enum PageTabs {
  OVERVIEW = 'OVERVIEW',
  FIXTURES = 'FIXTURES',
  SQUAD = 'SQUAD',
  CARDS = 'CARDS',
}

type ContextType = CountryQuery['football']['nationalTeam'];

export const useLayoutData = () => {
  return useOutletContext<ContextType>();
};

const tabsTitle: Record<PageTabs, MessageDescriptor> = {
  [PageTabs.OVERVIEW]: navLabels.overview,
  [PageTabs.FIXTURES]: navLabels.fixtures,
  [PageTabs.SQUAD]: navLabels.team,
  [PageTabs.CARDS]: glossary.cards,
};

export const Country = () => {
  const { countryCode } = useParams();
  const { params } = useSettlableWalletsSearch();
  const { data, loading } = useQuery(COUNTRY_QUERY, {
    variables: {
      countryCode: countryCode!,
    },
    skip: !countryCode,
  });

  const tabs = useMemo(() => {
    if (!countryCode) return {} as Record<PageTabs, string>;

    return {
      [PageTabs.OVERVIEW]: generatePath(FOOTBALL_COUNTRY_SHOW, {
        countryCode,
      }),
      [PageTabs.FIXTURES]: generatePath(FOOTBALL_COUNTRY_SHOW_FIXTURES, {
        countryCode,
      }),
      [PageTabs.SQUAD]: generatePath(FOOTBALL_COUNTRY_SHOW_TEAM, {
        countryCode,
      }),
      [PageTabs.CARDS]: generatePath(FOOTBALL_COUNTRY_SHOW_CARDS, {
        countryCode,
      }),
    };
  }, [countryCode]);

  if (loading) return <LoadingIndicator fullHeight />;
  if (!data) return null;
  const {
    football: { nationalTeam },
  } = data;

  const {
    country: { name: countryName },
  } = nationalTeam;

  return (
    <>
      <PageHeader text={countryName} />
      <ContentWithStickyHeader
        header={
          <FullWidthContainer.NoPadding>
            <TabBar variant="flat" fullWidth>
              {(Object.keys(tabs) as PageTabs[]).map(tab => (
                <Tab
                  key={tab}
                  to={
                    tab === PageTabs.CARDS
                      ? `${tabs[tab]}?${buildFilterQuery({
                          [SEARCH_PARAMS.ON_SALE]: true,
                          ...(params
                            ? { [SEARCH_PARAMS.PURCHASE_OPTIONS]: params }
                            : {}),
                        })}`
                      : tabs[tab]
                  }
                >
                  <FormattedMessage {...tabsTitle[tab]} />
                </Tab>
              ))}
            </TabBar>
          </FullWidthContainer.NoPadding>
        }
      >
        <Outlet context={nationalTeam satisfies ContextType} />
      </ContentWithStickyHeader>
    </>
  );
};

export default Country;
